import { Injectable } from "@angular/core";
import { memoizeSelector } from "@obg/ngx.global";
import { castArray, filter as lodashFilter, find, head, isEqual, map as lodashMap, some } from "lodash-es";
import { Observable } from "rxjs";
import { distinctUntilChanged, map } from "rxjs/operators";

import { CoreState } from "../state";

import { Message, MessageFilter, MessageType } from "./message.model";

// @dynamic
@Injectable({
	providedIn: "root",
})
export class MessageSelector {
	@memoizeSelector<CoreState>((state) => [state.message])
	has(filter: MessageFilter) {
		return (state: CoreState): boolean => some(state.message.messages, this.filter(filter));
	}

	isFatalError() {
		return (state: CoreState): boolean =>
			some(state.message.messages, (message) => message.type === MessageType.Fatal);
	}

	@memoizeSelector<CoreState>((state) => [state.message])
	getAll(filter?: MessageFilter) {
		return (state: CoreState): Message[] =>
			filter ? lodashFilter(state.message.messages, this.filter(filter)) : state.message.messages;
	}

	getAll$(filter?: MessageFilter) {
		return (state$: Observable<CoreState>): Observable<Message[]> =>
			state$.pipe(
				distinctUntilChanged(undefined!, (state) => state.message),
				map(this.getAll(filter)),
				distinctUntilChanged(isEqual)
			);
	}

	@memoizeSelector<CoreState>((state) => [state.message])
	getFirst(filter?: MessageFilter) {
		return (state: CoreState): Message | undefined =>
			filter ? find(state.message.messages, this.filter(filter)) : head(state.message.messages);
	}

	@memoizeSelector<CoreState>((state) => [state.message])
	getMessageCodes(filter?: MessageFilter) {
		return (state: CoreState): string[] => lodashMap(this.getAll(filter)(state), (message) => message.messageCode);
	}

	getMessageCodes$(filter?: MessageFilter) {
		return (state$: Observable<CoreState>): Observable<string[]> =>
			state$.pipe(
				distinctUntilChanged(undefined!, (state) => state.message),
				map(this.getMessageCodes(filter)),
				distinctUntilChanged(isEqual)
			);
	}

	private filter(filter: MessageFilter) {
		return (message: Message): boolean =>
			(!filter.messageCode || castArray(filter.messageCode).some((code) => code === message.messageCode)) &&
			(!filter.excludeCodes || !filter.excludeCodes.includes(message.messageCode)) &&
			(!filter.context || castArray(filter.context).some((msg) => msg === message.context)) &&
			(!filter.messageType || message.type === filter.messageType) &&
			(!filter.scope || message.scope === filter.scope);
	}
}
